import React, { useEffect, useState } from 'react';

import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Card,
    Collapse,
    Grid,
    IconButton,
    Tooltip,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { InfoToolTip } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormInputDatePicker,
    ReactHookFormSelect,
} from 'common/components/form';
import { DEBOUNCE_TIME_500 } from 'common/constants';

import * as actions from '../actions';
import * as selectors from '../selectors';

import InfoList from './InfoList';

const MIN_DATE = new Date(2005, 0, 1);

const Filters = ({
    companies,
    executives,
    fetchCompanies,
    fetchExecutives,
    filterQuery,
    setFilterQuery,
}) => {
    const { t } = useTranslation();
    const [timer, setTimer] = useState(null);
    const [filterIsOpen, setFilterIsOpen] = useState(false);

    const initialValues = { ...filterQuery };

    const { control, reset } = useForm({ defaultValues: initialValues });

    useEffect(() => reset(initialValues), []);

    const toggleFilter = () => setFilterIsOpen((prev) => !prev);

    const handleChange = (value) => {
        clearTimeout(timer);
        setTimer(setTimeout(() => triggerChange(value), DEBOUNCE_TIME_500));
    };

    const triggerChange = (value) => {
        setFilterQuery((prev) => ({ ...prev, search: value }));
    };

    const handleOnChangeValue = (value, name) => {
        setFilterQuery((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        fetchExecutives();
        fetchCompanies();
    }, []);

    return (
        <Card>
            <Card.Body>
                <form>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        <ReactHookFormInput
                            control={control}
                            name="search"
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder={t('Search')}
                            prepend={<SearchIcon />}
                            variant="outlined"
                        />

                        <Tooltip title={t('Filter', { count: 2 })}>
                            <IconButton onClick={toggleFilter} size="small">
                                <FilterListIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>

                        <InfoToolTip variant="no-wrapper">
                            <InfoList size="small" />
                        </InfoToolTip>
                    </Box>

                    <Collapse in={filterIsOpen}>
                        <Grid container sx={{ mt: 2 }}>
                            <Grid size={{ md: 5, xs: 18 }}>
                                <ReactHookFormSelect
                                    control={control}
                                    name="executive"
                                    label={t('Executive')}
                                    onChange={(e) =>
                                        handleOnChangeValue(
                                            e.target.value,
                                            'executive',
                                        )
                                    }
                                    options={executives}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={{ md: 5, xs: 18 }}>
                                <ReactHookFormSelect
                                    control={control}
                                    name="company"
                                    label={t('Company')}
                                    onChange={(e) =>
                                        handleOnChangeValue(
                                            e.target.value,
                                            'company',
                                        )
                                    }
                                    options={companies}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 18 }}>
                                <ReactHookFormInputDatePicker
                                    control={control}
                                    disableFuture
                                    label={t('Initial date')}
                                    minDate={MIN_DATE}
                                    name="initial_date"
                                    onChange={(e) =>
                                        handleOnChangeValue(e, 'initial_date')
                                    }
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 18 }}>
                                <ReactHookFormInputDatePicker
                                    control={control}
                                    disableFuture
                                    label={t('Final date')}
                                    minDate={MIN_DATE}
                                    name="final_date"
                                    onChange={(e) =>
                                        handleOnChangeValue(e, 'final_date')
                                    }
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </form>
            </Card.Body>
        </Card>
    );
};

const mapStateToProps = createStructuredSelector({
    companies: selectors.getCompaniesToSelect,
    executives: selectors.getExecutivesToSelect,
    filterItemSelected: selectors.getFilterItemSelected,
});

const mapDispatchToProps = (dispatch) => ({
    fetchExecutives: () => dispatch(actions.fetchExecutives()),
    fetchCompanies: () => dispatch(actions.fetchCompanies()),
});

Filters.propTypes = {
    companies: PropTypes.array,
    executives: PropTypes.array,
    fetchCompanies: PropTypes.func,
    fetchExecutives: PropTypes.func,
    filterQuery: PropTypes.object,
    setFilterQuery: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
